import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DiscussionError, type Reference } from '@peruselab/api-client/discussions';
import { Icon } from '../../components/Icon';
import { ReferencePicker } from './ReferencePicker';
import { statesAValue } from './references';

const MAX_LENGTH = 2000;

interface CommentComposerProps {
  readonly initial?: string;
  readonly submitLabel?: string;
  /** What the new comment will be about (ADR 0016 D2), e.g. "About: C a1, O a3"; clearable. */
  readonly anchorLabel?: string | null;
  readonly onClearAnchor?: () => void;
  /** #910: the comment's references when editing. */
  readonly initialReferences?: readonly Reference[];
  readonly onSubmit: (body: string, references: readonly Reference[]) => Promise<void>;
  readonly onCancel: () => void;
}

/** Writing or editing a comment: plain text with links (ADR 0016 phase 1), in any script. */
export function CommentComposer({
  initial = '',
  submitLabel,
  anchorLabel,
  onClearAnchor,
  initialReferences = [],
  onSubmit,
  onCancel
}: CommentComposerProps) {
  const { t } = useTranslation();
  const id = useId();
  const [body, setBody] = useState(initial);
  const [references, setReferences] = useState<readonly Reference[]>(initialReferences);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<DiscussionError['kind'] | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const trimmed = body.trim();
  // The composer only opens on a deliberate Reply / Edit / Add a comment, so focus follows it there.
  useEffect(() => textareaRef.current?.focus(), []);

  return (
    <form
      className="comment-composer"
      onSubmit={(event) => {
        event.preventDefault();
        if (!trimmed || busy) return;
        setBusy(true);
        setError(null);
        onSubmit(trimmed, references)
          .catch((failure: unknown) => setError(failure instanceof DiscussionError ? failure.kind : 'unknown'))
          .finally(() => setBusy(false));
      }}
    >
      {anchorLabel ? (
        <p className="comment-composer-anchor">
          <span>{anchorLabel}</span>
          {onClearAnchor ? (
            <button
              type="button"
              className="icon-button"
              aria-label={t('discussions.anchor.clear')}
              title={t('discussions.anchor.clear')}
              onClick={onClearAnchor}
            >
              <Icon name="close" size="1rem" />
            </button>
          ) : null}
        </p>
      ) : null}
      <label htmlFor={id} className="visually-hidden">
        {t('discussions.composerLabel')}
      </label>
      {/* Any language: the browser picks the direction from what's typed (multilingual, #897). */}
      <textarea
        id={id}
        dir="auto"
        value={body}
        maxLength={MAX_LENGTH}
        rows={3}
        placeholder={t('discussions.composerPlaceholder')}
        onChange={(event) => setBody(event.target.value)}
        ref={textareaRef}
      />
      <ReferencePicker references={references} onChange={setReferences} disabled={busy} />
      {/* D21: a nudge, never a gate -- a stated value without a source or data. */}
      {references.length === 0 && statesAValue(body) ? (
        <p className="comment-composer-nudge" role="note">
          <Icon name="info" size="1rem" />
          <span>{t('discussions.references.nudge')}</span>
        </p>
      ) : null}
      <div className="comment-composer-footer">
        <span className="comment-composer-count">
          {t('discussions.remaining', { count: MAX_LENGTH - body.length })}
        </span>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="icon-button"
          aria-label={t('discussions.cancel')}
          title={t('discussions.cancel')}
        >
          <Icon name="close" />
        </button>
        <button
          type="submit"
          className="icon-button"
          disabled={!trimmed || busy}
          aria-label={submitLabel ?? t('discussions.post')}
          title={submitLabel ?? t('discussions.post')}
        >
          <Icon name="send" />
        </button>
      </div>
      {error ? (
        <p className="import-error" role="alert">
          {t(`discussions.errors.${error}`)}
        </p>
      ) : null}
    </form>
  );
}
